import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  markGenerationToastShown,
  parseGenerationEvent,
  refreshGenerationDetail,
  shouldMuteGenerationToast,
  unacceptedOutcomes,
  upsertGenerationSummary,
  useGenerationWatch,
} from '@entities/Generation';
import { moduleKeys } from '@entities/Module';
import { planKeys } from '@entities/Plan';
import { useAuthStore } from '@entities/User';
import { wordKeys } from '@entities/Word';
import { forgetLookup, useWorkspaceStore } from '@features/CardsWorkspace';
import { toast } from '@shared/ui';
import {
  NotificationsStream,
  fetchUnreadCount,
  type NotificationDto,
} from '../api';
import {
  notificationsKeys,
  prependStreamedNotification,
  setToastBatchFlush,
  settleBatchedCard,
  useNotificationsStore,
} from '../model';
import { deferToast, releaseDeferred, type DeferredToasts } from '../model/deferredToasts';

/** Тост «готово» ждёт столько, чтобы нажатие «Создать» успело записать свои карточки в
 *  группу: быстрая сборка из словаря может прийти раньше ответа на POST. На главной столько же
 *  ждёт и тост об ошибке: SSE бывает раньше ответа, и карточки ещё нет в слежении (ТЗ главной § 8). */
const SUCCESS_TOAST_DELAY_MS = 1500;
/** Сколько слов перечислить в итоговом тосте группы. */
const BATCH_LIST = 4;
/** Список модулей (до 100 штук, `gcTime: 0`) перечитываем не на каждую карточку, а один раз
 *  после серии событий: у подборки их 20 подряд (ТЗ главной § 22.9). */
const MODULES_REFRESH_DELAY_MS = 500;

const TONE_TO_TOAST: Record<NotificationDto['tone'], 'info' | 'success' | 'error' | 'warning'> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

export function NotificationsListener() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const stage = useAuthStore((s) => s.stage);
  const setUnreadCount = useNotificationsStore((s) => s.setUnreadCount);
  const incrementUnread = useNotificationsStore((s) => s.incrementUnread);
  const clear = useNotificationsStore((s) => s.clear);
  const qc = useQueryClient();
  const streamRef = useRef<NotificationsStream | null>(null);
  // Защита от дубль-fetch'а в React StrictMode (effect → cleanup → effect).
  // Запоминаем accessToken, для которого уже инициализировали init-запросы.
  const initedForToken = useRef<string | null>(null);
  // Тост/unread — строго один раз на notificationId. При кратком наложении двух
  // SSE-подписок (реконнект) Redis fan-out может доставить один и тот же ивент
  // несколько раз; список дедупит prependStreamedNotification, тост/счётчик — этот сет.
  const seenNotifIds = useRef<Set<string>>(new Set());
  // Тосты о карточках, которые главная не показала (ТЗ главной § 8): ушёл с главной, не приняв итог, — они
  // показываются при уходе, чтобы сбой не потерялся.
  const deferred = useRef<DeferredToasts<NotificationDto>>(new Map());
  const { t } = useTranslation();

  /** «Готово 5 карточек» и первые слова — итог нажатия (и отложенных успехов главной). */
  const showBatch = (fronts: string[]) => {
    const shown = fronts.slice(0, BATCH_LIST).join(', ');
    const rest = fronts.length - BATCH_LIST;
    toast({
      tone: 'success',
      title: t('notifications.batchReady', { count: fronts.length }),
      message: rest > 0 ? t('notifications.batchMore', { list: shown, count: rest }) : shown,
    });
  };
  const showRef = useRef(showBatch);
  showRef.current = showBatch;

  // Итоговый тост нажатия «Создать»: «Готово 5 карточек» и первые слова. На главной итог группы не
  // показываем, если все её карточки — из отслеживаемых генераций: его покажут запись и сигнал у строки;
  // он откладывается до ухода с главной.
  useEffect(() => {
    setToastBatchFlush((cards) => {
      const cardIds = cards.map((card) => card.cardId);
      if (shouldMuteGenerationToast(cardIds)) {
        for (const card of cards) deferToast(deferred.current, card.cardId, { kind: 'success', front: card.front });
        return;
      }
      markGenerationToastShown(cardIds);
      showRef.current(cards.map((card) => card.front));
    });
    return () => setToastBatchFlush(null);
  }, []);

  // Ушёл с главной (ТЗ § 8, «сбой не теряется»): тосты нажатий этого захода, итог которых ученик не принял, —
  // показать сейчас: сбои — каждый, как обычно; успехи — одним итогом (и только если «Готово» не появлялось
  // на виду). Остальное отложенное отбрасывается: итог принят, либо это прошлый заход — его покажет запись.
  useEffect(
    () =>
      useGenerationWatch.subscribe((state, prev) => {
        if (!prev.homeVisible || state.homeVisible) return;
        const stash = deferred.current;
        deferred.current = new Map();
        if (stash.size === 0) return;
        // Выход из аккаунта — тостов прошлого ученика не показываем.
        const auth = useAuthStore.getState();
        if (auth.stage !== 'app' || !auth.accessToken) return;
        const outcomes = unacceptedOutcomes(state);
        const released = releaseDeferred(stash, outcomes.cardIds, outcomes.unseenReadyCardIds);
        if (released.cardIds.length === 0) return;
        // Не изнутри уведомления стора: пометка `announced` — это снова запись в тот же стор.
        queueMicrotask(() => {
          markGenerationToastShown(released.cardIds);
          for (const dto of released.failures) toast({ tone: TONE_TO_TOAST[dto.tone], title: dto.title, message: dto.body });
          const [only] = released.successes;
          if (released.successes.length === 1 && only.toast) {
            toast({ tone: TONE_TO_TOAST[only.toast.tone], title: only.toast.title, message: only.toast.body });
          } else if (released.successes.length > 0) {
            showRef.current(released.successes.map((success) => success.front));
          }
        });
      }),
    [],
  );

  useEffect(() => {
    if (stage !== 'app' || !accessToken) {
      streamRef.current?.stop();
      streamRef.current = null;
      initedForToken.current = null;
      seenNotifIds.current.clear();
      deferred.current = new Map();
      clear();
      return;
    }

    let cancelled = false;
    let stream: NotificationsStream | null = null;
    let modulesTimer: number | undefined;
    const refreshModules = () => {
      window.clearTimeout(modulesTimer);
      modulesTimer = undefined;
      void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
    };
    // Отложенный сброс списков модулей: последний вызов серии.
    const refreshModulesSoon = () => {
      window.clearTimeout(modulesTimer);
      modulesTimer = window.setTimeout(refreshModules, MODULES_REFRESH_DELAY_MS);
    };

    // Init-запрос unread-count фаерим только раз для конкретного accessToken —
    // иначе StrictMode дважды дёргает эндпоинт.
    if (initedForToken.current !== accessToken) {
      initedForToken.current = accessToken;

      fetchUnreadCount()
        .then((count) => {
          if (!cancelled) setUnreadCount(count);
        })
        .catch(() => {
          // Сетевые ошибки уже покрывает баннер про сервер.
        });
    }

    queueMicrotask(() => {
      if (cancelled) return;
      stream = new NotificationsStream(
        () => useAuthStore.getState().accessToken,
        {
          onNotification: (n) => {
            // Список дедупится по id внутри prependStreamedNotification — зовём всегда.
            prependStreamedNotification(qc, n);
            // Тост/unread — один раз на notificationId (защита от дубль-доставки).
            if (seenNotifIds.current.has(n.id)) return;
            seenNotifIds.current.add(n.id);
            if (!n.isRead) incrementUnread();
            const show = () =>
              toast({
                tone: TONE_TO_TOAST[n.tone],
                title: n.title,
                message: n.body,
              });
            // Тост о карточке отслеживаемой генерации на главной не показываем — итог виден в записи
            // «Недавно добавленных» и в сигнале у строки (ТЗ главной § 8): он откладывается и покажется,
            // если ученик уйдёт с главной, не приняв итог. Решаем в момент показа; показанный помечаем
            // `announced`. Колокольчик и группы тостов — как обычно.
            const showCard = (cardId: string) => {
              if (shouldMuteGenerationToast([cardId])) {
                deferToast(
                  deferred.current,
                  cardId,
                  n.eventType === 'succeeded' ? { kind: 'success', front: n.front ?? '', toast: n } : { kind: 'failure', toast: n },
                );
                return;
              }
              show();
              markGenerationToastShown([cardId]);
            };
            if (n.cardId && n.eventType === 'succeeded') {
              // Карточка из нажатия с несколькими карточками — тост уйдёт в общий итог.
              const cardId = n.cardId;
              window.setTimeout(() => {
                if (!settleBatchedCard(cardId, true)) showCard(cardId);
              }, SUCCESS_TOAST_DELAY_MS);
            } else if (n.cardId) {
              const cardId = n.cardId;
              settleBatchedCard(cardId, false);
              // На главной ошибка тоже ждёт 1,5 с: SSE бывает раньше ответа «Создать», и тогда карточки ещё
              // нет в слежении. На других страницах — сразу, как раньше.
              if (useGenerationWatch.getState().homeVisible) window.setTimeout(() => showCard(cardId), SUCCESS_TOAST_DELAY_MS);
              else showCard(cardId);
            } else {
              show();
            }
            void qc.invalidateQueries({ queryKey: notificationsKeys.unreadCount() });
          },
          onCardEvent: (meta) => {
            // Триггер рефреша для страниц, которые слушают card-события
            // (Cards Workspace перезапрашивает cards + stats для текущего модуля).
            useWorkspaceStore.getState().bumpRefresh();
            // Модули могли поменяться (новая карточка → +1 слово, изменился статус
            // прогресса). Списки модулей — один раз после серии событий, byId — ниже.
            refreshModulesSoon();
            // Ошибка генерации возвращает слот дневной квоты (refund в воркере) —
            // счётчики AI в композере и на главной перечитываем.
            void qc.invalidateQueries({ queryKey: planKeys.quota });
            void qc.invalidateQueries({ queryKey: planKeys.billing });
            if (meta.moduleId) {
              void qc.invalidateQueries({ queryKey: wordKeys.byUnit(meta.moduleId) });
              void qc.invalidateQueries({ queryKey: moduleKeys.byId(meta.moduleId) });
            }
            // Пометки строк подсказки («генерируется» → «уже учится») для этого слова
            // устарели — открытый поиск этого слова тут же тихо перечитает их с бэка.
            if (meta.card?.frontName) forgetLookup(meta.card.frontName);
            if (meta.cardId) {
              useWorkspaceStore.getState().applyCardEvent({
                type: meta.eventType,
                cardId: meta.cardId,
                suggestions: meta.suggestions,
                errorInfo: meta.errorInfo,
                card: meta.card
                  ? {
                      transcription: meta.card.transcription,
                      cefr: meta.card.cefr,
                      primaryTranslation: meta.card.primaryTranslation,
                      meanings: meta.card.meanings,
                      status: meta.card.status ?? null,
                    }
                  : null,
              });
            }
          },
          onGenerationUpdated: (payload) => {
            // Сводка записи «Генераций» — в кэш списка, детали и слежения. Открытую деталь
            // перечитываем, только если сменились статус, счётчики или жетоны, не чаще раза в 1,5 с.
            const event = parseGenerationEvent(payload);
            if (!event) return;
            if (upsertGenerationSummary(qc, event.generation).changed) {
              refreshGenerationDetail(qc, event.generation.id);
            }
          },
        },
      );
      stream.start();
      streamRef.current = stream;
    });

    return () => {
      cancelled = true;
      // Отложенный сброс не теряем, если поток пересоздаётся (новый токен).
      if (modulesTimer !== undefined) refreshModules();
      stream?.stop();
      streamRef.current = null;
    };
  }, [accessToken, stage, qc, setUnreadCount, incrementUnread, clear]);

  return null;
}
